import type { Asset, FilesApi } from '@odo/shared-ui';
import type { InvalidateQueryFilters, QueryClient, QueryKey } from '@tanstack/react-query';
import {
    ADMIN_APPOINTMENTS_BASE_KEY,
    DM_MESSAGES_INBOX_KEY,
    DM_NOTIFICATIONS_KEY,
    HOME_MY_CASES_BASE_KEY,
    PATIENT_ASSETS_KEY,
    PATIENT_CARD_KEY,
    PATIENTS_KEY,
    WORK_QUEUE_KEY
} from './queryKeys.ts';

// Patient list pages share the PATIENTS_KEY root with every per-patient query,
// so match the list shapes (page number or 'infinite') rather than the prefix.
const isPatientListKey = (queryKey: QueryKey): boolean =>
    queryKey[0] === PATIENTS_KEY && (queryKey[1] === 'infinite' || typeof queryKey[1] === 'number');

/** Every cached query that renders this patient's avatar. */
export const profilePictureQueryFilters = (patientId: string): InvalidateQueryFilters[] => [
    { queryKey: PATIENT_CARD_KEY(patientId), exact: true },
    { predicate: (query) => isPatientListKey(query.queryKey) },
    { queryKey: [WORK_QUEUE_KEY, 'cases'] },
    { queryKey: [WORK_QUEUE_KEY, 'cases-infinite'] },
    { queryKey: [WORK_QUEUE_KEY, 'case', patientId] },
    { queryKey: [WORK_QUEUE_KEY, 'search'] },
    { queryKey: HOME_MY_CASES_BASE_KEY },
    { queryKey: ADMIN_APPOINTMENTS_BASE_KEY },
    { queryKey: [DM_NOTIFICATIONS_KEY] },
    { queryKey: [DM_MESSAGES_INBOX_KEY] }
];

interface QueryInvalidator {
    invalidateQueries(filters: InvalidateQueryFilters): Promise<void>;
}

export const invalidateProfilePictureQueries = async (
    client: QueryInvalidator,
    patientId: string
): Promise<void> => {
    await Promise.all(
        profilePictureQueryFilters(patientId).map((filters) => client.invalidateQueries(filters))
    );
};

const PROFILE_PICTURE_STORAGE_CATEGORY = 'PROFILE_PICTURE';

export const isProfilePictureFile = (
    assets: Readonly<Record<string, Asset>> | undefined,
    patientFileId: string
): boolean =>
    Object.values(assets ?? {}).some(
        (asset) =>
            asset.patientFileId === patientFileId &&
            asset.storageCategory === PROFILE_PICTURE_STORAGE_CATEGORY
    );

type DeleteFile = FilesApi['deleteFile'];

// The category is read before deleting: the post-delete refresh drops the file from the cache.
export const withAvatarRefreshOnDelete =
    (client: QueryClient, deleteFile: DeleteFile): DeleteFile =>
    async (patientId, patientFileId) => {
        const assets = client.getQueryData<Record<string, Asset>>(PATIENT_ASSETS_KEY(patientId));
        await deleteFile(patientId, patientFileId);
        if (isProfilePictureFile(assets, patientFileId)) {
            void invalidateProfilePictureQueries(client, patientId);
        }
    };
